import React, { FC, useContext } from 'react';
import styled from 'styled-components';

import FileCheck from '../../assets/icons/file-check.svg';
import { SignInContext } from '../../SignInContext';
import {
  APPLY_FLOW_IDENTITY_EMAIL,
  colors,
  MOBILE_BREAKPOINT,
} from '../constants';
import { ifFeatureFlagOn } from '../utilities/feature-flags';
import { setUserIdentityToSessionStorage } from '../utilities/identity';
import { removeJWTFromLocalStorage } from '../utilities/jwt';
import { pxToRem } from '../utilities/utility';

import { PrimaryButton, InlineButton } from './Button';
import Heading from './Heading';
import { ModalWrapper, ModalProps } from './ModalWrapper';

// Styles -----------------------------------------------------------------------------------------
const SignInModalContent = styled.div`
  position: relative;
  width: 22.75rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 0.25rem;
  padding: 2.25rem 1.5rem;
  background: #ffffff;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 3rem 0;
    width: 100%;
  }
  @media (max-height: ${MOBILE_BREAKPOINT}) {
    width: 35rem;
    height: 18.4375rem;
    padding: 1.5rem;
  }
`;
SignInModalContent.displayName = 'SignInModalContent';

const FileCheckIcon = styled(FileCheck)`
  width: 3.625rem;
  height: 3.3125rem;
  margin: 0 auto;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0 auto 1.8125rem;
  }
`;
FileCheckIcon.displayName = 'FileCheckIcon';

const ModalHeading = styled(Heading)`
  margin: 1.75rem auto 0;
  padding: 0 1rem;
  white-space: pre-line;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0;
    padding: 0 1.5rem;
  }
`;
ModalHeading.displayName = 'ModalHeading';

const SubText = styled.p`
  padding: 0 1rem;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5rem;
  text-align: center;
  color: ${colors.TEXT_PRIMARY};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 2rem;
  }
`;
SubText.displayName = 'SubText';

const ContinueButton = styled(PrimaryButton)`
  width: 19rem;
  margin: 2.125rem 0 1.9375rem;
  padding: 1.0625rem 0;
  line-height: 1.25rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 2rem 1.25rem;
  }
`;
ContinueButton.displayName = 'ContinueButton';

const ContinueTextButton = styled(InlineButton)`
  margin: 0;
  width: 4.375rem;
  color: ${colors.TEXT_PRIMARY};
`;
ContinueTextButton.displayName = 'ContinueTextButton';

const UsingDifferentEmailButton = styled(InlineButton)`
  color: ${colors.TEXT_ANCHOR};
  font-weight: 600;
  font-size: ${pxToRem(16)};
  text-decoration: none;
`;
UsingDifferentEmailButton.displayName = 'UsingDifferentEmailButton';

// Component --------------------------------------------------------------------------------------
export const SignInModalControl: FC<ModalProps> = ({
  showModal,
  setShowModal,
}) => {
  const {
    userEmail,
    setUserEmail,
    externalUserId,
    firstName,
    lastName,
    dateOfBirth,
  } = useContext(SignInContext);

  // Functions ------------------------------------------------------------------------------------
  const redirectToSignInPage = () => {
    removeJWTFromLocalStorage();
    // Construct a return url from the current url
    const returnURL = `${window.location.pathname}${window.location.search}`;
    // Start building the web-authentication url
    let redirectURL = `/auth/email-code?redirect_uri=${encodeURIComponent(
      returnURL
    )}`;
    // Possibly add query params to fill out the sign in page
    if (externalUserId || userEmail) {
      redirectURL += externalUserId
        ? `&external_user_id=${externalUserId}`
        : `&email=${userEmail}`;
    }
    if (ifFeatureFlagOn(APPLY_FLOW_IDENTITY_EMAIL)) {
      // capture entered firstName, lastName, dateOfBirth for validating against loggedIn user
      setUserIdentityToSessionStorage({
        firstName,
        lastName,
        dateOfBirth,
        isVerified: false,
      });
    }
    // Send them to the email-code sign in page
    window.location.assign(redirectURL);
  };

  // Hide the modal when the user explicitly chooses not to sign in
  const continueApplying = () => {
    setUserEmail(null);
    setShowModal(false);
  };

  // Render ---------------------------------------------------------------------------------------
  return (
    <ModalWrapper
      id="signin-modal"
      showModal={showModal}
      setShowModal={setShowModal}
      shouldReturnFocusAfterClose={false}
      disallowClose
    >
      <SignInModalContent
        tabIndex={-1}
        aria-labelledby="modal-heading"
        aria-modal="true"
        role="dialog"
      >
        <FileCheckIcon aria-hidden="true" />
        <ModalHeading id="modal-heading">
          Do you already
          <br />
          have an account?
        </ModalHeading>
        <ContinueButton
          id="signin-modal-continue-button"
          onClick={redirectToSignInPage}
          aria-label="Log in to continue"
          type="button"
          role="link"
        >
          Log in to continue
        </ContinueButton>
        <UsingDifferentEmailButton
          id="continue-without-signin-button"
          aria-label="Continue"
          onClick={continueApplying}
          type="button"
          role="button"
        >
          Or use a different email
        </UsingDifferentEmailButton>
      </SignInModalContent>
    </ModalWrapper>
  );
};
